import { useEffect, useState } from 'react';

import { MtxButton } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import { CarrouselItem } from '@/api';
import { SlideMark } from '@/assets/icons/slideMark';
import { SlideMarkActive } from '@/assets/icons/slideMarkActive';
import placeholderPng from '@/assets/images/placeholder.png';
import Image from '@/components/Image/Image';
import { Skeleton } from '@/components/skeleton/Skeleton';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';

import styles from './styles.module.css';

interface CarrouselProps {
  data: CarrouselItem[];
  isLoading: boolean;
}

const Carrousel: React.FC<CarrouselProps> = ({ data, isLoading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const { windowWidth } = useScreenSize();
  const { t } = useTranslation();
  const totalSlides = data.length;

  const skeletonImgWidth = windowWidth < 600 ? 305 : 280;
  const skeletonImgHeight = windowWidth < 600 ? 107 : 145;

  const onClickCarrousel = (currentIndex: number) => {
    globalThis.open(data[currentIndex].lien ?? undefined, '_blank', 'noopener,noreferrer');

    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'placement',
      event_nom: 'decouvrir',
      event_detail_1: 'nos_offres_du_moment',
      event_detail_2: data[currentIndex]?.tracking,
    });
  };

  const getSlideImage = (index: number) => {
    if (windowWidth < 600) {
      return data[index]?.imageMobile || placeholderPng;
    }
    return data[index]?.imageDesktop || placeholderPng;
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalSlides - 1 : prevIndex - 1));
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'carrousel fleche gauche',
      event_detail_1: 'picto',
    });
  };

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'carrousel pause',
      event_detail_1: 'picto',
    });
  };

  const isDataMissing = (index: number) => {
    const slide = data[index];
    return (
      !slide?.titre ||
      !slide.sousTitre ||
      !slide.mentions ||
      !slide.imageDesktop ||
      !slide.imageMobile ||
      !slide.imageAlt ||
      !slide.lien ||
      !slide.tracking ||
      !slide.dateDebut
    );
  };

  const displayDataIfSlideIsFull = (index: number, key: keyof CarrouselItem, returnIfMissing: string = '') => {
    if (!isDataMissing(index)) {
      return data[index][key];
    }
    return returnIfMissing;
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [data]);

  const nextSlideWithTracking = () => {
    nextSlide();
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'carrousel fleche droite',
      event_detail_1: 'picto',
    });
  };

  useEffect(() => {
    if (isPlaying && totalSlides > 0) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, currentIndex, nextSlide, totalSlides]);

  const currentSlideMissing = totalSlides > 0 && isDataMissing(currentIndex);
  const isSlideLinkUnavailable = isLoading || displayDataIfSlideIsFull(currentIndex, 'lien') === '';

  const handleSlideClick = () => {
    if (data[currentIndex]?.lien) onClickCarrousel(currentIndex);
  };

  return (
    <div className={styles.carrousel}>
      <div className={styles.carrouselTopContent}>
        <div className={styles.carrouselTextContent}>
          <div className={styles.carrouselTextTop}>
            <div>
              <span className={styles.carrouselText}>
                {isLoading ? (
                  <Skeleton height={24} />
                ) : (
                  displayDataIfSlideIsFull(currentIndex, 'titre', t('carrousel.missingDataTitle'))
                )}
              </span>
            </div>
            <span className={styles.carrouselDownText}>
              {isLoading ? (
                <Skeleton height={16} />
              ) : (
                displayDataIfSlideIsFull(currentIndex, 'sousTitre', t('carrousel.missingDataDescription'))
              )}
            </span>
          </div>

          <span className={styles.carrouselMentions}>
            {isLoading ? (
              <>
                <Skeleton height={12} />
                <Skeleton height={12} width="85%" />
              </>
            ) : (
              displayDataIfSlideIsFull(currentIndex, 'mentions')
            )}
          </span>
        </div>

        <div className={styles.carrouselPicture}>
          {isLoading ? (
            <Skeleton height={skeletonImgHeight} width={skeletonImgWidth} />
          ) : (
            <Image
              className={currentSlideMissing ? styles.cover : ''}
              defaultSrc={currentSlideMissing ? placeholderPng : getSlideImage(currentIndex)}
              alt={currentSlideMissing ? 'Illustration offre carrousel' : (data[currentIndex]?.imageAlt ?? '')}
            />
          )}
        </div>
      </div>

      <div className={styles.carrouselBottomContent}>
        <MtxButton
          variant="secondary"
          size="medium"
          className={styles.carrouselCta}
          fullWidth={windowWidth < 600}
          onClick={handleSlideClick}
          disabled={isSlideLinkUnavailable}
        >
          {t('carrousel.labelButton')}
        </MtxButton>

        <div className={styles.carrouselController}>
          <div className={styles.carrouselControllerContent}>
            <button onClick={prevSlide} className={styles.carrouselSlideContent}>
              <i className={`totem-icon totem-icon-chevron-left-big ${styles.carrousselBtn}`} />
            </button>

            <div className={styles.carrouselSlideContent}>
              {data.map((slide, index) =>
                index === currentIndex ? (
                  <SlideMarkActive key={`${slide.titre}${slide.imageDesktop}`} />
                ) : (
                  <SlideMark key={`${slide.titre}${slide.imageDesktop}`} />
                )
              )}
            </div>

            <div className={styles.carrouselPlayPause}>
              <button className={styles.carrouselPlayButton} onClick={togglePlayPause}>
                <i className={`totem-icon ${isPlaying ? 'totem-icon-pause' : 'totem-icon-play'}`} />
              </button>
            </div>

            <button onClick={nextSlideWithTracking} className={styles.carrouselSlideContent}>
              <i className={`totem-icon totem-icon-chevron-right-big ${styles.carrousselBtn}`} />
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className={styles.carrouselOverlay}
        onClick={handleSlideClick}
        disabled={isSlideLinkUnavailable}
      />
    </div>
  );
};

export default Carrousel;
